import { walletApiSlice } from '../apiSlice';

export const DISCOUNT_TYPE = { PERCENTAGE: 1, FIXED: 2 } as const;

export type IDiscountType = (typeof DISCOUNT_TYPE)[keyof typeof DISCOUNT_TYPE];

export interface IGetCouponsListRequest {
	pageNumber?: number;
	pageSize?: number;
	code?: string;
	isActive?: boolean;
	includeExpired?: boolean;
	/**
	 * Server-side "only expired" filter. Backend support pending — until it ships,
	 * the Coupon page filters expired coupons client-side (see useCouponProps).
	 */
	isExpired?: boolean;
	createdFromDate?: string;
	createdToDate?: string;
	expiryFromDate?: string;
	expiryToDate?: string;
}

export interface ICoupon {
	id: string;
	code: string;
	description: string;
	discountType: number;
	discountValue: number;
	maxDiscountAmountNaira: number | null;
	isActive: boolean;
	expiresAt: string | null;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	isExpired: boolean;
}

export interface IGetCouponListResponse {
	items: ICoupon[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
	totalPages: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
}

interface ICreateCouponRequest {
	code: string;
	description: string;
	discountType: IDiscountType;
	discountValue: number;
	maxDiscountAmountNaira: number | null;
	expiresAt: string | null;
	conditions: unknown[];
	createdBy: string;
}

interface IClaimedCouponResponse {
	items: {
		id: string;
		couponId: string;
		walletId: string;
		userId: string;
		tenantId: string;
		claimedAt: string;
		code: string;
		description: string;
		discountType: number;
		discountValue: number;
		maxDiscountAmountNaira: null;
		isActive: boolean;
		expiresAt: string;
		isExpired: boolean;
	}[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
	totalPages: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
}

interface IUpdateCouponRequest extends ICreateCouponRequest {
	id: string;
}

interface IValidateCouponRequest {
	couponCode: string;
	paymentAmountNaira: number;
	isPaymentContext: boolean;
	walletId: string;
}

interface IValidateCouponResponse {
	isValid: boolean;
	errorMessage: string | null;
	couponId: string;
	couponCode: string;
	discountType: number;
	operation: number;
	paymentAmountNaira: number;
	couponValueNaira: number;
	payableAmountNaira: number;
	creditComputationAmountNaira: number;
	conversionRateUsed: number;
	projectedBaseCredits: number;
	projectedFinalCredits: number;
	projectedCreditDelta: number;
}

/**
 * Echoed back by `/activate` and `/deactivate`.
 *
 * <p>Named `…Request` historically and used as the RESPONSE type of both endpoints, which is the
 * only shape either has ever had. Renamed to `…Result` because nothing sends this — the request is
 * just the id in the path.</p>
 */
export interface IActivateDeactivateCouponResult {
	couponId: string;
	code: string;
	isActive: boolean;
	updatedAt: string;
}

const couponApiSlice = walletApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getCouponsList: builder.mutation<IGetCouponListResponse, IGetCouponsListRequest>({
			query: (body: IGetCouponsListRequest) => ({ url: '/coupons/list', method: 'POST', body }),
		}),
		createCoupon: builder.mutation<{ code: string; couponId: string }, ICreateCouponRequest>({
			query: (body: ICreateCouponRequest) => ({ url: '/coupons', method: 'POST', body }),
		}),
		broadcastCoupon: builder.mutation<
			{
				couponId: string;
				code: string;
				userIds: string[];
				sentAt: string;
			},
			{ message: string; couponId: string; userIds: string[] }
		>({
			query: ({ message, userIds, couponId }) => ({
				method: 'POST',
				url: `/coupons/${couponId}/broadcast`,
				body: {
					message,
					userIds,
				},
			}),
		}),
		updateCoupon: builder.mutation<{ code: string; couponId: string }, IUpdateCouponRequest>({
			query: ({ id, ...body }) => ({ url: `/coupons/${id}`, method: 'PUT', body }),
		}),
		deleteCoupon: builder.mutation<undefined, string>({
			query: (id) => ({ url: `/coupons/${id}`, method: 'DELETE' }),
		}),
		claimCoupon: builder.mutation<unknown, { couponId: string; walletId: string; userId: string }>({
			query: ({ couponId, ...body }) => ({
				url: `/coupons/${couponId}/claim`,
				method: 'POST',
				body,
			}),
		}),
		getClaimedCoupons: builder.mutation<
			IClaimedCouponResponse,
			{ walletId: string; includeExpired?: boolean; pageNumber?: number; pageSize?: number }
		>({
			query: ({ walletId, includeExpired, pageNumber, pageSize }) => ({
				url: '/coupons/claimed',
				method: 'POST',
				body: { walletId, includeExpired, pageNumber, pageSize },
			}),
		}),
		validateCoupon: builder.mutation<IValidateCouponResponse, IValidateCouponRequest>({
			query: (body) => ({
				url: '/coupons/validate',
				method: 'POST',
				body,
			}),
		}),
		/**
		 * Flip a coupon's `isActive`. Both endpoints take nothing but the id in the path.
		 *
		 * <p><b>`method` was omitted on both until 2026-09-09</b>, so RTK Query issued a <b>GET</b>
		 * for a state change. Neither hook had a single call site in the app, so this had never
		 * run — the defect was invisible because the feature was never wired up. POST is asserted
		 * from consistency with every sibling state-changing coupon endpoint on this service
		 * (`/claim`, `/broadcast`, and the schedule `/pause` + `/resume`), not from the service
		 * source, which is not available in this repo. <b>Verify against a running wallet-service
		 * before release</b>; a 404/405 here means the verb, not the path.</p>
		 */
		activateCoupon: builder.mutation<IActivateDeactivateCouponResult, { couponId: string }>({
			query: ({ couponId }) => ({
				url: `/coupons/${couponId}/activate`,
				method: 'POST',
			}),
		}),
		deactivateCoupon: builder.mutation<IActivateDeactivateCouponResult, { couponId: string }>({
			query: ({ couponId }) => ({
				url: `/coupons/${couponId}/deactivate`,
				method: 'POST',
			}),
		}),
	}),
});

export const {
	useGetCouponsListMutation,
	useCreateCouponMutation,
	useUpdateCouponMutation,
	useDeleteCouponMutation,
	useBroadcastCouponMutation,
	useClaimCouponMutation,
	useGetClaimedCouponsMutation,
	useValidateCouponMutation,
	useActivateCouponMutation,
	useDeactivateCouponMutation,
} = couponApiSlice;
